// #ux-redesign-start: legalSections (Paso 5 — Auth redesign)
// Define la lista de secciones legales que aparecen en el Login. Cada sección
// se renderiza:
//   - Desktop: dentro del panel izquierdo del AuthLayout (panelContent).
//   - Mobile: dentro de un Modal del DS.
// El contenido textual proviene del LegalContent legacy (preservado intacto)
// y de TermsCardContent (i18n del namespace `terms-card`).
//
// PORTABILIDAD: trasplantable a Web-Patients.
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import { TranslatorProvider } from 'react-translate';
import TermsCardContent from 'views/Components/Terms/TermsCardContent';

const DatenschutzSection = () => (
  <article className="ds-auth__legal-section">
    <h3>Allgemeine Informationen</h3>
    <p>
      Die TelePraxen Plattform, das Online-Portal für Medizin + Gesundheit, ist
      ein Service- und Informationsportal für Patienten und
      Gesundheitsdienstleister. Ärzte, Zahnärzte, Therapeuten und
      Gesundheitseinrichtungen stellen sich und Ihre Leistungen in Ihrer
      Online-Praxis vor.
    </p>
    <ul>
      <li>Die TelePraxen APP dient dem sicheren Datenaustausch innerhalb und zwischen Gesundheitsdienstleistern, sowie zwischen Gesundheitsdienstleistern und Patienten.</li>
      <li>Die Buchungs-Online-System (BOS) bietet Patienten die Möglichkeit, online-Termine mit Gesundheitsdienstleistern zu vereinbaren.</li>
      <li>Das Ferndiagnostikmodul (FDM) bietet Patienten die Möglichkeit, ferndiagnostische Untersuchungen zu vereinbaren.</li>
    </ul>

    <h3>Registrierung / Personenbezogene Daten</h3>
    <p>
      Soweit auf unseren Seiten personenbezogene Daten (zum Beispiel Name,
      Anschrift oder E-Mail-Adresse) über Sie als Nutzer erhoben werden,
      erfolgt dies im freiwilligen Einvernehmen. Die personenbezogenen Daten
      werden gemäß den gesetzlichen Bestimmungen der DSGVO, des
      Bundesdatenschutzgesetzes und des Telemediengesetzes behandelt.
    </p>
    <p>
      Eine Weitergabe der Daten an Dritte erfolgt ohne Ihre Einwilligung nur,
      wenn wir hierzu gesetzlich verpflichtet sind. Es wird darauf
      hingewiesen, dass die Datenübertragung im Internet
      Sicherheitslücken aufweisen kann.
    </p>

    <h3>Cookies</h3>
    <p>
      Wie viele andere Betreiber von Webseiten verwenden wir auch so genannte
      „Cookies“. Sie können die Installation der Cookies durch eine
      entsprechende Einstellung Ihrer Browser-Software verhindern. Wir weisen
      Sie jedoch darauf hin, dass Sie in diesem Fall gegebenenfalls nicht
      sämtliche Funktionen dieser Website vollumfänglich nutzen können.
    </p>

    <h3>Ihre Rechte</h3>
    <p>
      Sie haben das Recht, jederzeit Auskunft über Ihre bei uns gespeicherten
      personenbezogenen Daten zu erhalten, sowie auf Berichtigung, Sperrung
      oder Löschung. Wenden Sie sich dazu an{' '}
      <a href="mailto:datenschutz@telepraxen.de">datenschutz@telepraxen.de</a>.
    </p>

    <p className="ds-auth__legal-footer">
      Alpha Research Deutschland GmbH · Goslarer Platz 7, 10589 Berlin<br />
      Geschäftsführer: Priv.-Doz. Dr. med. Heinz R. Zurbrügg · HRB-Nr.: AG Charlottenburg 58 0 57
    </p>
  </article>
);

const MeineDatenSection = () => (
  <article className="ds-auth__legal-section">
    <h3>Einverständniserklärung</h3>
    <p>
      Ich erkläre mich bis auf schriftlichen Widerruf einverstanden mit der
      Verarbeitung und Speicherung meiner Daten inkl. medizinischer
      Informationen bei den TelePraxen-Ärzten und -Zahnärzten nach den
      gesetzlichen Vorgaben zur Verwendung dieser innerhalb der TelePraxen
      oder durch Vertragspraxen.
    </p>
    <p>
      Die TelePraxen-Ärzte und -Zahnärzte beauftragen die Alpha Research
      Deutschland GmbH mit der Speicherung und Archivierung personenbezogener
      Daten gemäß den gesetzlichen Bestimmungen der DSGVO und des
      Bundesdatenschutzgesetzes.
    </p>
    <p>
      Die TelePraxen oder Verrechnungsstellen werden die Abrechnung der
      ärztlichen/therapeutischen Leistungen vornehmen.
    </p>
    <p>
      Weitergehende Informationen zum Thema Datenschutz erhalten Sie unter{' '}
      <a href="https://www.telepraxen.de/datenschutz" target="_blank" rel="noreferrer">
        www.telepraxen.de/datenschutz
      </a>.
    </p>

    <h3>Recht auf Auskunft, Berichtigung, Löschung und Sperrung</h3>
    <p>
      Sie haben das Recht auf Auskunft, Berichtigung, Löschung und Sperrung
      Ihrer Daten — wenn das nicht den gesetzlichen Vorschriften widerspricht.
      Zur Wahrnehmung dieser Rechte können Sie uns jederzeit unter{' '}
      <a href="mailto:datenschutz@telepraxen.de">datenschutz@telepraxen.de</a>{' '}
      kontaktieren.
    </p>
  </article>
);

const AgbSection = ({ language }) => (
  <article className="ds-auth__legal-section">
    <TranslatorProvider
      translations={require(`assets/i18n/terms/${language || 'de'}.json`)}
    >
      <TermsCardContent />
    </TranslatorProvider>
  </article>
);

const ImpressumSection = () => (
  <article className="ds-auth__legal-section">
    <h3>Beauftragter für Medizinproduktesicherheit</h3>
    <p>
      Priv.-Doz. Dr. med. Heinz R. Zurbrügg<br />
      E-Mail: <a href="mailto:info@telepraxen.de">info@telepraxen.de</a><br />
      Telefon: +49 30 32 79 80 92 20 · Telefax: +49 30 32 79 80 97
    </p>

    <h3>Verantwortlich gemäß TMG</h3>
    <p>
      Alpha Research Deutschland GmbH<br />
      Goslarer Platz 7, 10589 Berlin<br />
      Geschäftsführer: Priv.-Doz. Dr. med. Heinz R. Zurbrügg<br />
      HRB-Nr.: AG Charlottenburg 58 0 57 · USt.-ID-Nr.: DE 179 221 234<br />
      E-Mail: <a href="mailto:impressum@alpha-research.com">impressum@alpha-research.com</a><br />
      Telefon: +49 30 32 79 80 90
    </p>

    <p>
      Die im Rahmen dieser Website benannten Ärzte sind Mitglied der
      Ärztekammer Berlin. Die benannten Zahnärzte sind Mitglieder der
      Zahnärztekammer Berlin.
    </p>

    <h3>Bild-, Urheber- und Quellenangabe</h3>
    <p>
      Unter dem folgenden Link finden Sie alle Quellen, von denen wir
      Nutzungsrechte bezogen und auf diesen Seiten verwendet haben:{' '}
      <a href="https://telepraxen.de/quellenangabe/" target="_blank" rel="noreferrer">
        telepraxen.de/quellenangabe
      </a>.
    </p>

    <h3>Disclaimer / Haftungsausschluss</h3>
    <p>
      Es wird keine Garantie für die Vollständigkeit, die Richtigkeit und die
      Aktualität der auf dieser Webseite bereitgestellten Informationen
      übernommen. Für die Inhalte externer Internetseiten, die durch einen
      Link erreicht werden, wird keine Haftung übernommen.
    </p>

    <p className="ds-auth__legal-footer">
      Es gilt deutsches Recht. Gerichtsstand ist Berlin, Deutschland.<br />
      Copyright © Alpha Research Deutschland GmbH
    </p>
  </article>
);

// Labels traducibles — el caller pasa los strings ya traducidos (mismo patrón
// que ConfirmDialog / PasskeyButton). Fallback a German legal terminology si
// no se proveen.
export const buildLegalSections = (language, labels = {}) => [
  { id: 'datenschutz', label: labels.datenschutz || 'Datenschutz',  content: <DatenschutzSection /> },
  { id: 'meine-daten', label: labels.meineDaten  || 'Meine Daten',  content: <MeineDatenSection /> },
  { id: 'agb',         label: labels.agb         || "AGB's",        content: <AgbSection language={language} /> },
  { id: 'impressum',   label: labels.impressum   || 'Impressum',    content: <ImpressumSection /> },
];

// #ux-redesign-end
